import { AbstractControl, UntypedFormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import dayjs from 'dayjs';

export function requiredMinLengthCustomValidator(lengthRe: number): ValidatorFn {
  return (control: AbstractControl): { [key: string]: boolean } | null => {
    const forbidden = String(control.value) && String(control.value).length >= lengthRe ? true : false;
    return forbidden ? null : { requiredMinLengthCustom: true };
  };
}

export function requiredMaxLengthCustomValidator(lengthRe: number): ValidatorFn {
  return (control: AbstractControl): { [key: string]: boolean } | null => {
    const forbidden = String(control.value) && String(control.value).length <= lengthRe ? true : false;
    return forbidden ? null : { requiredMaxLengthCustom: true };
  };
}
export function requiredAtLeastOneUpperCaseValidator(nameRe: RegExp): ValidatorFn {
  return (control: AbstractControl): { [key: string]: boolean } | null => {
    const forbidden = nameRe.test(control.value);
    return forbidden ? null : { requiredAtLeastOneUpperCase: true };
  };
}

export function requiredAtLeastOneLowerCaseValidator(nameRe: RegExp): ValidatorFn {
  return (control: AbstractControl): { [key: string]: boolean } | null => {
    const forbidden = nameRe.test(control.value);
    return forbidden ? null : { requiredAtLeastOneLowerCase: true };
  };
}

export function requiredAtLeastOneSpecialCharacterValidator(nameRe: RegExp): ValidatorFn {
  return (control: AbstractControl): { [key: string]: boolean } | null => {
    const forbidden = nameRe.test(control.value);
    return forbidden ? null : { requiredAtLeastOneSpecialCharacter: true };
  };
}
export function requiredAtLeastOneNumericValidator(nameRe: RegExp): ValidatorFn {
  return (control: AbstractControl): { [key: string]: boolean } | null => {
    const forbidden = nameRe.test(control.value);
    return forbidden ? null : { requiredAtLeastOneNumeric: true };
  };
}

export function requiredNoSpaceValidator(valueRe: RegExp): ValidatorFn {
  return (control: AbstractControl): { [key: string]: boolean } | null => {
    const forbidden = valueRe.test(control.value);
    return forbidden ? null : { requiredNoSpace: true };
  };
}

export function requiredEmailValidator(valueRe: RegExp): ValidatorFn {
  return (control: AbstractControl): { [key: string]: boolean } | null => {
    const forbidden = valueRe.test(String(control.value).toLowerCase());
    return forbidden ? null : { requiredEmail: true };
  };
}

export function emailRegexValidator(valueRe: RegExp, valueRequired: boolean): ValidatorFn {
  return (control: AbstractControl): { [key: string]: boolean } | null => {
    const forbidden = valueRe.test(String(control.value).toLowerCase());
    return forbidden && control.value ? null : valueRequired ? { requiredEmail: true } : control.value ? { InvalidEmail: true } : null;
  };
}

export function noWhitespaceValidator(value: boolean): ValidatorFn {
  if (value) {
    return (control: AbstractControl): { [key: string]: string } => {
      const isWhitespace = (control.value || '').trim().length === 0;
      const isValid = !isWhitespace;
      return isValid ? null : { whitespace: 'value is only whitespace' };
    };
  } else {
    return (control: AbstractControl): { [key: string]: string } => {
      const isWhitespace = (control.value || '').trim().length === 0;
      if (control.value && control.value.length) {
        const isValid = !isWhitespace;
        return isValid ? null : { whitespace: 'value is only whitespace' };
      } else {
        return null;
      }
    };
  }
}

export function allowMaxValue(maxValue: number): ValidatorFn {
  return (control: AbstractControl): { [key: string]: boolean } | null => {
    const allow = maxValue >= control.value;
    return allow ? null : { allowMaxValue: true };
  };
}

export function allowMinValue(minValue: number): ValidatorFn {
  return (control: AbstractControl): { [key: string]: boolean } | null => {
    const allow = minValue <= control.value;
    return allow ? null : { allowMinValue: true };
  };
}

export function atLeastOne(validator: ValidatorFn): ValidatorFn {
  return (group: AbstractControl): ValidationErrors | null => {
    if (!group) {
      return null;
    }

    const controls = getAllControlsInFormGroup(group as UntypedFormGroup);
    const hasAtLeastOne = controls.some((control) => !validator(control));
    return hasAtLeastOne ? null : { atLeastOne: true };
  };
}

function getAllControlsInFormGroup(group: UntypedFormGroup): AbstractControl[] {
  const flattenedControls = [];
  for (const key of Object.keys(group.controls)) {
    const control = group.controls[key];
    if (control instanceof UntypedFormGroup) {
      flattenedControls.push(...getAllControlsInFormGroup(control));
    } else {
      flattenedControls.push(control);
    }
  }
  return flattenedControls;
}

export function validateDate(dateFormat: string): ValidatorFn {
  return (control: AbstractControl): { [key: string]: boolean } | null => {
    if (!control.value) {
      return { dateInvalid: true };
    }
    if (control.value.length !== dateFormat.length) {
      return { dateInvalid: true };
    }
    return dayjs(control.value, dateFormat, true).isValid() ? null : { dateInvalid: true };
  };
}

export function validateAddressLine(): ValidatorFn {
  const addressLineMatcher = /(\r\n|\r|\n|\t)/g;
  return (control: AbstractControl): { [key: string]: boolean } | null => {
    if (!control.value) {
      return null;
    }
    if (addressLineMatcher.exec(control.value)) {
      return {
        addressLineInvalid: true,
      };
    }
    return null;
  };
}

export function validateSpecifiedGender(): ValidatorFn {
  const allowedCharacters = /^[a-zA-Z0-9\-\\/+,& ]+$/;
  const disallowedCharacters = /[\n\r\t]/;
  return (control: AbstractControl): { [key: string]: boolean } | null => {
    if (!disallowedCharacters.exec(control?.value) && allowedCharacters.exec(control?.value)) {
      return null;
    } else if (control?.value?.length) {
      return { invalidSpecifiedGender: true };
    }
  };
}

export function validateNonRequiredEmail(): ValidatorFn {
  const emailRegex = /^[A-Z0-9_'%=+!`#~$*?^{}&|-]+([.][A-Z0-9_'%=+!`#~$*?^{}&|-]+)*@[A-Z0-9-]+(\.[A-Z0-9-]+)+$/i;
  return (control: AbstractControl): { [key: string]: boolean } | null => {
    if (control?.value?.length && !emailRegex.test(control?.value)) {
      return { email: true };
    }
    return null;
  };
}

export function maxDate(maxDateString: string, dateFormat: string): ValidatorFn {
  return (control: AbstractControl): { [key: string]: boolean } | null => {
    if (control.value && control.value.length === dateFormat.length && dayjs(control.value, dateFormat).isValid()) {
      const isValid = dayjs(maxDateString, dateFormat).isSameOrAfter(dayjs(control.value, dateFormat));
      return isValid ? null : { afterMaxDate: true };
    }
    return { dateInvalidMax: true };
  };
}

export function minDate(minDateString: string, dateFormat: string): ValidatorFn {
  return (control: AbstractControl): { [key: string]: boolean } | null => {
    if (control.value && control.value.length === dateFormat.length && dayjs(control.value, dateFormat).isValid()) {
      const isValid = dayjs(minDateString, dateFormat).isSameOrBefore(dayjs(control.value, dateFormat));
      return isValid ? null : { beforeMinDate: true };
    }
    return { dateInvalidMin: true };
  };
}
